import React, { useState, useEffect } from 'react';
import { User as UserType } from '@supabase/supabase-js';
import {
  User,
  Shield,
  LogOut,
  Lock,
  Mail,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Gift,
  Clock,
  ChevronRight,
  Save,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  Award,
  Globe
} from 'lucide-react';
import TwoFactorSetup from './TwoFactorSetup';
import KycDocumentUpload from './KycDocumentUpload';
import SupportChat from './SupportChat';
import LanguageSwitcher from './LanguageSwitcher';
import PhoneInput from './PhoneInput';
import ReferralTab from './ReferralTab';
import GiveawaySection from './GiveawaySection';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from 'react-i18next';
import { getUserCfdTier } from '../constants/tradingTiers';
import { UserStatus } from '../App';

interface ProfilePageProps {
  user: UserType;
  usdtBalance?: number;
  btcBalance?: number;
  currentPrice?: number;
  onSignOut: () => void;
  onUpdatePassword: (password: string) => Promise<any>;
  referralCode: string | null;
  referralCount: number;
  referredUsers: any[];
  totalPortfolioValue: number;
  totalPositionsPnl?: number;
  userStatus: UserStatus;
  kycStatus: 'not_verified' | 'pending' | 'verified';
  updateKycStatus: (status: 'not_verified' | 'pending' | 'verified') => void;
  portfolioSnapshots?: any[];
  createPortfolioSnapshot?: () => Promise<void>;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  user,
  usdtBalance,
  btcBalance,
  currentPrice,
  onSignOut,
  onUpdatePassword,
  referralCode,
  referralCount,
  referredUsers,
  totalPortfolioValue,
  totalPositionsPnl = 0,
  userStatus,
  kycStatus: propKycStatus,
  updateKycStatus: propUpdateKycStatus,
  portfolioSnapshots,
  createPortfolioSnapshot
}) => {
  const { t } = useTranslation();
  const { twoFactorEnabled, disableTwoFactor } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'referrals' | 'giveaway' | 'support'>('profile');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [showKycUpload, setShowKycUpload] = useState(false);
  const [kycStatus, setKycStatus] = useState<'not_verified' | 'pending' | 'verified'>(propKycStatus || 'not_verified');

  // Profile information state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const userCfdTier = getUserCfdTier(totalPortfolioValue);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, country, phone_number, kyc_status')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }

        if (data) {
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setCountry(data.country || '');

          if (data.phone_number) {
            const match = data.phone_number.match(/^(\+\d+)\s*(.*)$/);
            if (match) {
              setCountryCode(match[1]);
              setPhoneNumber(match[2]);
            } else {
              setPhoneNumber(data.phone_number);
            }
          }

          setKycStatus(data.kyc_status || 'not_verified');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, [user.id]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { error } = await onUpdatePassword(newPassword);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!twoFactorCode) {
      setError('Please enter your two-factor authentication code');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await disableTwoFactor(twoFactorCode);
      
      if (result.error) {
        throw result.error;
      }
      
      setSuccess('Two-factor authentication disabled successfully');
      setTwoFactorCode('');
      setDisabling2FA(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setSuccess('Referral code copied to clipboard');
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError(null);
    setSuccess(null);
    
    try {
      const fullPhoneNumber = phoneNumber ? `${countryCode} ${phoneNumber}` : '';

      const { error } = await supabase
        .from('users')
        .update({
          first_name: firstName,
          last_name: lastName,
          country: country,
          phone_number: fullPhoneNumber
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Profile information saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile information');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleStartVerification = () => {
    setShowKycUpload(true);
  };

  const handleKycStatusChange = (status: 'not_verified' | 'pending' | 'verified') => {
    setKycStatus(status);
    propUpdateKycStatus(status);
  };

  const homeBackgroundClass =
    'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_30%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.2),transparent_32%),radial-gradient(circle_at_bottom_center,rgba(217,70,239,0.14),transparent_38%)]';
  const primaryCardClass =
    'rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/24 via-indigo-500/18 to-purple-500/16 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
  const secondaryCardClass =
    'rounded-2xl border border-purple-500/35 bg-gradient-to-br from-blue-500/22 via-indigo-500/16 to-purple-500/18 shadow-2xl shadow-purple-500/15 backdrop-blur-sm';
  const innerCardClass =
    'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12';
  const innerCardHoverClass =
    'transition-all duration-300 hover:border-purple-400/45 hover:from-indigo-500/16 hover:via-purple-500/16 hover:to-fuchsia-500/16';
  const tabRailClass =
    'mb-8 flex flex-wrap gap-2 rounded-2xl border border-purple-500/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/10 to-fuchsia-500/12 p-2 backdrop-blur-sm';
  const activeTabClass =
    'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25';
  const inactiveTabClass =
    'border border-purple-500/20 bg-purple-950/35 text-slate-300 transition-all duration-300 hover:bg-purple-500/10 hover:border-purple-400/40 hover:text-white';
  const sectionIconClass =
    'flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25';
  const inputClass =
    'purple-form-field w-full rounded-xl border border-purple-500/30 bg-gradient-to-r from-indigo-950/72 via-purple-950/72 to-fuchsia-950/70 px-4 py-3 text-white placeholder:text-slate-400 transition-all focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/55';
  const inputWithLeftIconClass =
    'purple-form-field w-full rounded-xl border border-purple-500/30 bg-gradient-to-r from-indigo-950/72 via-purple-950/72 to-fuchsia-950/70 py-3 text-white placeholder:text-slate-400 transition-all focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/55';
  const primaryActionButtonClass =
    'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:from-purple-500 hover:to-fuchsia-500 disabled:from-slate-700 disabled:to-slate-800';
  const neutralActionButtonClass =
    'border border-purple-500/25 bg-purple-950/45 text-slate-200 transition-all duration-300 hover:bg-purple-500/12 hover:border-purple-400/40 hover:text-white';
  const dangerActionButtonClass =
    'border border-rose-400/30 bg-rose-500/15 text-rose-100 transition-all duration-300 hover:bg-rose-500/25 hover:border-rose-300/40';
  const subtleTextClass = 'text-slate-300';
  const mutedTextClass = 'text-slate-400';

  const portfolioStatusTiers = [
    { name: 'No-Coiner', threshold: '$0 - $249', badge: 'NC', accentClass: 'from-slate-200 to-indigo-200' },
    { name: 'Shrimp', threshold: '$250+', badge: 'SH', accentClass: 'from-rose-200 to-orange-200' },
    { name: 'Crab', threshold: '$10K+', badge: 'CR', accentClass: 'from-orange-200 to-amber-200' },
    { name: 'Octopus', threshold: '$25K+', badge: 'OC', accentClass: 'from-amber-200 to-emerald-200' },
    { name: 'Dolphin', threshold: '$50K+', badge: 'DL', accentClass: 'from-emerald-200 to-cyan-200' },
    { name: 'Shark', threshold: '$100K+', badge: 'SK', accentClass: 'from-cyan-200 to-blue-200' },
    { name: 'Whale', threshold: '$500K+', badge: 'WH', accentClass: 'from-blue-200 to-violet-200' },
    { name: 'Humpback', threshold: '$2M+', badge: 'HB', accentClass: 'from-violet-200 to-pink-200' }
  ];

  const cfdTierCards = [
    { name: 'Starter', minEquity: '$0', maxForex: 100, maxCommodities: 30, maxStocks: 20, badge: 'ST' },
    { name: 'Plus', minEquity: '$10K', maxForex: 200, maxCommodities: 50, maxStocks: 40, badge: 'PL' },
    { name: 'Advanced', minEquity: '$50K', maxForex: 300, maxCommodities: 75, maxStocks: 60, badge: 'AD' },
    { name: 'Pro', minEquity: '$100K', maxForex: 500, maxCommodities: 90, maxStocks: 80, badge: 'PR' },
    { name: 'Elite', minEquity: '$250K', maxForex: 1000, maxCommodities: 100, maxStocks: 100, badge: 'EL' }
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className={`pointer-events-none absolute inset-0 ${homeBackgroundClass}`}></div>
      <div className="pointer-events-none absolute -left-20 top-0 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-3xl"></div>
      <div className="pointer-events-none absolute right-0 top-8 h-96 w-96 rounded-full bg-indigo-500/12 blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25">
            <User size={32} className="text-white" />
          </div>
          <div>
            <div className="mb-2 inline-flex items-center rounded-full border border-purple-500/20 bg-purple-950/35 px-3 py-1 text-xs uppercase tracking-[0.24em] text-purple-200">
              Profile Hub
            </div>
            <h1 className="bg-gradient-to-r from-indigo-100 via-violet-100 to-fuchsia-100 bg-clip-text text-3xl font-bold text-transparent">
              {t('profile.accountSettings')}
            </h1>
            <p className={mutedTextClass}>{user.email}</p>
          </div>
        </div>
        
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className={`px-6 py-4 ${secondaryCardClass}`}>
            <div className={`text-sm ${mutedTextClass}`}>Portfolio Value</div>
            <div className="text-white font-mono text-xl">${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            {totalPositionsPnl !== 0 && (
              <div className="flex items-center gap-1 mt-1">
                {totalPositionsPnl >= 0 ? (
                  <TrendingUp size={14} className="text-emerald-400" />
                ) : (
                  <TrendingDown size={14} className="text-red-400" />
                )}
                <span className={`text-sm font-medium ${totalPositionsPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {totalPositionsPnl >= 0 ? '+' : ''}{totalPositionsPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                </span>
              </div>
            )}
          </div>

          <button
            onClick={onSignOut}
            className={`flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-medium ${dangerActionButtonClass}`}
          >
            <LogOut size={18} />
            {t('navigation.signOut')}
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className={tabRailClass}>
        <button
          onClick={() => setActiveTab('profile')}
          className={`rounded-xl px-3 py-2 text-xs font-medium md:px-6 md:py-3 md:text-sm ${
            activeTab === 'profile' 
              ? activeTabClass
              : inactiveTabClass
          }`}
        >
          <div className="flex items-center gap-2">
            <User size={18} />
            {t('profile.profileInformation')}
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('security')}
          className={`rounded-xl px-3 py-2 text-xs font-medium md:px-6 md:py-3 md:text-sm ${
            activeTab === 'security' 
              ? activeTabClass
              : inactiveTabClass
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            {t('profile.security')}
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('referrals')}
          className={`rounded-xl px-3 py-2 text-xs font-medium md:px-6 md:py-3 md:text-sm ${
            activeTab === 'referrals'
              ? activeTabClass
              : inactiveTabClass
          }`}
        >
          <div className="flex items-center gap-2">
            <Gift size={18} />
            {t('profile.referrals')}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('giveaway')}
          className={`rounded-xl px-3 py-2 text-xs font-medium md:px-6 md:py-3 md:text-sm ${
            activeTab === 'giveaway'
              ? activeTabClass
              : inactiveTabClass
          }`}
        >
          <div className="flex items-center gap-2">
            <Gift size={18} />
            Giveaway
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('support')}
          className={`rounded-xl px-3 py-2 text-xs font-medium md:px-6 md:py-3 md:text-sm ${
            activeTab === 'support' 
              ? activeTabClass
              : inactiveTabClass
          }`}
        >
          <div className="flex items-center gap-2">
            <MessageCircle size={18} />
            {t('profile.support')}
          </div>
        </button>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-red-500/40 bg-red-500/12 p-4">
          <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-500/35 bg-emerald-500/12 p-4">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{success}</span>
        </div>
      )}
      
      {/* Tab Content */}
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        {/* Left Column - Main Content */}
        <div className="xl:col-span-2">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className={`p-8 ${primaryCardClass}`}>
              <h2 className="mb-6 flex items-center gap-3 text-xl font-semibold text-white">
                <div className={sectionIconClass}>
                  <User size={20} className="text-white" />
                </div>
                {t('profile.profileInformation')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedTextClass}`} />
                    <input
                      type="email"
                      value={user.email || ''}
                      readOnly
                      className={`${inputWithLeftIconClass} pl-10 pr-4`}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('auth.firstName')}</label>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  
                  <div>
                    <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('auth.lastName')}</label>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
                
                <PhoneInput
                  value={phoneNumber}
                  countryCode={countryCode}
                  onCountryCodeChange={setCountryCode}
                  onPhoneNumberChange={setPhoneNumber}
                />

                <div>
                  <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('auth.country')}</label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={`${inputClass} custom-select purple-form-select`}
                  >
                    <option value="">{t('profile.selectCountry')}</option>
                    <option value="US">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="JP">Japan</option>
                    <option value="SG">Singapore</option>
                    <option value="CH">Switzerland</option>
                    <option value="NL">Netherlands</option>
                    <option value="ES">Spain</option>
                    <option value="IT">Italy</option>
                    <option value="SE">Sweden</option>
                    <option value="NO">Norway</option>
                    <option value="DK">Denmark</option>
                    <option value="FI">Finland</option>
                    <option value="NZ">New Zealand</option>
                    <option value="BR">Brazil</option>
                    <option value="MX">Mexico</option>
                    <option value="IN">India</option>
                    <option value="CN">China</option>
                    <option value="RU">Russia</option>
                    <option value="ZA">South Africa</option>
                    <option value="AE">United Arab Emirates</option>
                  </select>
                </div>

                {/* Language Preferences */}
                <div className={`p-6 ${innerCardClass}`}>
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
                    <Globe size={20} className="text-purple-200" />
                    Language Preferences
                  </h3>
                  <p className={`mb-4 text-sm ${mutedTextClass}`}>
                    Select your preferred language for the platform interface
                  </p>
                  <LanguageSwitcher variant="auth" />
                </div>

                {/* Portfolio Status Tiers */}
                <div className={`p-6 ${secondaryCardClass}`}>
                  <div className="text-center mb-6">
                    <h3 className="mb-2 bg-gradient-to-r from-indigo-100 via-violet-100 to-fuchsia-100 bg-clip-text text-2xl font-bold text-transparent">
                      Portfolio Status Tiers
                    </h3>
                    <p className={`text-sm ${mutedTextClass}`}>
                      Your status is determined by your total portfolio value
                    </p>
                  </div>

                  {/* Current Status Badge */}
                  <div className="mb-6 rounded-xl border border-purple-400/30 bg-gradient-to-r from-indigo-500/16 via-purple-500/16 to-fuchsia-500/16 p-4 text-center">
                    <div className={`mb-1 text-sm ${mutedTextClass}`}>Your Current Status</div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={24} className="text-purple-200" />
                      <span className="bg-gradient-to-r from-indigo-100 to-fuchsia-100 bg-clip-text text-3xl font-bold text-transparent">
                        {userStatus}
                      </span>
                    </div>
                    <div className={`mt-2 text-lg font-semibold ${subtleTextClass}`}>
                      ${totalPortfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Status Tiers Grid */}
                  <div className="space-y-3">
                    {[
                      { name: 'No-Coiner', threshold: '$0 - $249', color: 'from-slate-500 to-slate-600', icon: '🪙', emoji: '💸' },
                      { name: 'Shrimp', threshold: '$250+', color: 'from-red-500 to-orange-500', icon: '🦐', emoji: '🦐' },
                      { name: 'Crab', threshold: '$10K+', color: 'from-orange-500 to-yellow-500', icon: '🦀', emoji: '🦀' },
                      { name: 'Octopus', threshold: '$25K+', color: 'from-yellow-500 to-green-500', icon: '🐙', emoji: '🐙' },
                      { name: 'Dolphin', threshold: '$50K+', color: 'from-green-500 to-cyan-500', icon: '🐬', emoji: '🐬' },
                      { name: 'Shark', threshold: '$100K+', color: 'from-cyan-500 to-blue-500', icon: '🦈', emoji: '🦈' },
                      { name: 'Whale', threshold: '$500K+', color: 'from-blue-500 to-purple-500', icon: '🐋', emoji: '🐋' },
                      { name: 'Humpback', threshold: '$2M+', color: 'from-purple-500 to-pink-500', icon: '🐳', emoji: '🐳' },
                    ].map((tier) => (
                      <div
                        key={tier.name}
                        className={`relative rounded-xl border p-4 transition-all duration-300 ${
                          tier.name === userStatus
                            ? 'border-purple-300/55 bg-gradient-to-r from-indigo-500/20 via-purple-500/18 to-fuchsia-500/18 shadow-lg shadow-purple-500/20 ring-2 ring-purple-400/20'
                            : `${innerCardClass} ${innerCardHoverClass}`
                        }`}
                      >
                        {tier.name === userStatus && (
                          <div className="absolute -right-2 -top-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2 py-1 text-xs font-bold text-white shadow-lg shadow-purple-500/25">
                            YOU
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">{tier.emoji}</div>
                          <div className="flex-1">
                            <div className={`font-bold text-lg bg-gradient-to-r ${tier.color} bg-clip-text text-transparent`}>
                              {tier.name}
                            </div>
                            <div className="text-slate-400 text-sm">{tier.threshold} Portfolio Value</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progress Info */}
                  <div className="mt-6 rounded-xl border border-purple-400/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12 p-4">
                    <p className={`text-center text-sm ${subtleTextClass}`}>
                      Keep growing your portfolio to reach higher status tiers and unlock exclusive benefits
                    </p>
                  </div>
                </div>

                {/* CFD Trading Tiers */}
                <div className={`p-6 ${secondaryCardClass}`}>
                  <div className="text-center mb-6">
                    <h3 className="mb-2 bg-gradient-to-r from-indigo-100 via-violet-100 to-fuchsia-100 bg-clip-text text-2xl font-bold text-transparent">
                      CFD Trading Tiers
                    </h3>
                    <p className={`text-sm ${mutedTextClass}`}>
                      Grow your portfolio to unlock higher leverage limits and better trading conditions
                    </p>
                  </div>

                  {/* Current Tier Badge */}
                  <div className="mb-6 rounded-xl border border-purple-400/30 bg-gradient-to-r from-indigo-500/16 via-purple-500/16 to-fuchsia-500/16 p-4 text-center">
                    <div className={`mb-1 text-sm ${mutedTextClass}`}>Your Current Tier</div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={24} className="text-purple-200" />
                      <span className="bg-gradient-to-r from-indigo-100 to-fuchsia-100 bg-clip-text text-3xl font-bold text-transparent">
                        {userCfdTier.name}
                      </span>
                    </div>
                    <div className={`mt-2 text-sm ${mutedTextClass}`}>{userCfdTier.description}</div>
                  </div>

                  {/* Tier Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {[
                      { name: 'Starter', minEquity: '$0', maxForex: 100, maxCommodities: 30, maxStocks: 20, icon: '📊' },
                      { name: 'Plus', minEquity: '$10K', maxForex: 200, maxCommodities: 50, maxStocks: 40, icon: '📈' },
                      { name: 'Advanced', minEquity: '$50K', maxForex: 300, maxCommodities: 75, maxStocks: 60, icon: '💹' },
                      { name: 'Pro', minEquity: '$100K', maxForex: 500, maxCommodities: 90, maxStocks: 80, icon: '🏆' },
                      { name: 'Elite', minEquity: '$250K', maxForex: 1000, maxCommodities: 100, maxStocks: 100, icon: '👑' },
                    ].map((tier) => (
                      <div
                        key={tier.name}
                        className={`relative rounded-xl border p-4 transition-all duration-300 ${
                          tier.name === userCfdTier.name
                            ? 'border-purple-300/55 bg-gradient-to-r from-indigo-500/20 via-purple-500/18 to-fuchsia-500/18 shadow-lg shadow-purple-500/20 ring-2 ring-purple-400/20'
                            : `${innerCardClass} ${innerCardHoverClass}`
                        }`}
                      >
                        {tier.name === userCfdTier.name && (
                          <div className="absolute -right-2 -top-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2 py-1 text-xs font-bold text-white shadow-lg shadow-purple-500/25">
                            ACTIVE
                          </div>
                        )}
                        <div className="text-center mb-3">
                          <div className="text-4xl mb-2">{tier.icon}</div>
                          <div className="text-white font-bold text-lg">{tier.name}</div>
                          <div className="text-slate-400 text-xs">Min. {tier.minEquity}</div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400">Forex:</span>
                            <span className="text-emerald-400 font-bold">{tier.maxForex}x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400">Commodities:</span>
                            <span className="font-bold text-violet-200">{tier.maxCommodities}x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400">Stocks:</span>
                            <span className="text-purple-400 font-bold">{tier.maxStocks}x</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Your Limits */}
                  <div className="rounded-xl border border-purple-400/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12 p-4">
                    <h4 className="mb-3 flex items-center gap-2 font-semibold text-white">
                      <TrendingUp size={18} className="text-purple-200" />
                      Your Current Maximum Leverage
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-fuchsia-200">{userCfdTier.maxForex}x</div>
                        <div className={`mt-1 text-xs ${mutedTextClass}`}>Forex</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-violet-200">{userCfdTier.maxCommodities}x</div>
                        <div className={`mt-1 text-xs ${mutedTextClass}`}>Commodities</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-indigo-200">{userCfdTier.maxStocks}x</div>
                        <div className={`mt-1 text-xs ${mutedTextClass}`}>Stocks</div>
                      </div>
                    </div>
                  </div>

                  <div className={`mt-4 text-center text-xs ${mutedTextClass}`}>
                    💡 Your leverage limits are automatically adjusted based on your total portfolio value
                  </div>
                </div>

                {/* KYC Section */}
                <div id="kyc-section" className={`p-6 ${innerCardClass}`}>
                  <h3 className="mb-4 text-lg font-semibold text-white">{t('profile.kycVerification')}</h3>
                  <p className={`mb-4 ${mutedTextClass}`}>
                    {t('profile.kycDescription')}
                  </p>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      kycStatus === 'verified' ? 'bg-green-500/20' : 
                      kycStatus === 'pending' ? 'bg-amber-500/20' : 
                      'bg-amber-500/20'
                    }`}>
                      {kycStatus === 'verified' ? (
                        <CheckCircle size={20} className="text-green-400" />
                      ) : kycStatus === 'pending' ? (
                        <Clock size={20} className="text-amber-400" />
                      ) : (
                        <AlertTriangle size={20} className="text-amber-400" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {kycStatus === 'verified' ? t('profile.verificationComplete') : 
                         kycStatus === 'pending' ? t('profile.verificationPending') : 
                         t('profile.verificationRequired')}
                      </div>
                      <div className={`text-sm ${mutedTextClass}`}>
                        {kycStatus === 'verified' ? t('profile.accountVerified') : 
                         kycStatus === 'pending' ? t('profile.documentsReviewed') : 
                         t('profile.accountNotVerified')}
                      </div>
                    </div>
                  </div>
                  
                  {kycStatus === 'not_verified' && (
                    <button 
                      onClick={handleStartVerification}
                      className={`flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold ${primaryActionButtonClass}`}
                    >
                      {t('profile.startVerification')}
                      <ChevronRight size={18} />
                    </button>
                  )}
                  
                  {kycStatus === 'pending' && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-400 text-sm">
                      {t('profile.verificationPending')}
                    </div>
                  )}
                  
                  {kycStatus === 'verified' && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400 text-sm">
                      {t('profile.verificationComplete')}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className={`flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold ${primaryActionButtonClass}`}
                  >
                    {savingProfile ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        {t('profile.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              {/* Change Password */}
              <div className={`p-8 ${primaryCardClass}`}>
                <h2 className="mb-6 flex items-center gap-3 text-xl font-semibold text-white">
                  <div className={sectionIconClass}>
                    <Lock size={20} className="text-white" />
                  </div>
                  {t('profile.changePassword')}
                </h2>
                
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div>
                    <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('profile.currentPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedTextClass}`} />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('profile.enterCurrentPassword')}
                        className={`${inputWithLeftIconClass} pl-10 pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${mutedTextClass} transition-colors hover:text-white`}
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('profile.newPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedTextClass}`} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('profile.enterNewPassword')}
                        className={`${inputWithLeftIconClass} pl-10 pr-10`}
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${mutedTextClass} transition-colors hover:text-white`}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className={`ml-1 mt-1 text-xs ${mutedTextClass}`}>
                      {t('profile.passwordMinLength')}
                    </p>
                  </div>
                  
                  <div>
                    <label className={`mb-2 block text-sm ${mutedTextClass}`}>{t('profile.confirmNewPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedTextClass}`} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className={`${inputWithLeftIconClass} pl-10 pr-10`}
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${mutedTextClass} transition-colors hover:text-white`}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading || !newPassword || !confirmPassword}
                      className={`flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold ${primaryActionButtonClass}`}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        t('profile.updatePassword')
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Two-Factor Authentication */}
              <div className={`p-8 ${primaryCardClass}`}>
                <h2 className="mb-6 flex items-center gap-3 text-xl font-semibold text-white">
                  <div className={sectionIconClass}>
                    <Shield size={20} className="text-white" />
                  </div>
                  {t('profile.twoFactorAuth')}
                </h2>
                
                {showTwoFactorSetup ? (
                  <TwoFactorSetup
                    onComplete={() => {
                      setShowTwoFactorSetup(false);
                      setSuccess('Two-factor authentication enabled successfully');
                      window.location.reload();
                    }}
                    onCancel={() => setShowTwoFactorSetup(false)}
                  />
                ) : disabling2FA ? (
                  <form onSubmit={handleDisableTwoFactor} className="space-y-6">
                    <p className={`mb-4 ${subtleTextClass}`}>
                      {t('profile.enterTwoFactorCodeToDisable')}
                    </p>
                    
                    <div>
                      <label className={`mb-2 block text-sm ${mutedTextClass}`}>
                        {t('profile.verificationCode')}
                      </label>
                      <input
                        type="text"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        placeholder="Enter 6-digit code"
                        className={inputClass}
                        maxLength={6}
                        pattern="[0-9]{6}"
                        required
                      />
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => setDisabling2FA(false)}
                        className={`rounded-xl px-6 py-3 font-medium ${neutralActionButtonClass}`}
                      >
                        {t('common.cancel')}
                      </button>
                      
                      <button
                        type="submit"
                        disabled={loading || twoFactorCode.length !== 6}
                        className={`flex items-center gap-2 rounded-xl px-6 py-3 font-semibold ${dangerActionButtonClass}`}
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          t('profile.disableTwoFactor')
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className="text-white font-medium mb-1">
                          {twoFactorEnabled ? t('profile.twoFactorEnabledStatus') : t('profile.twoFactorNotEnabledStatus')}
                        </p>
                        <p className={`text-sm ${mutedTextClass}`}>
                          {twoFactorEnabled ? t('profile.accountProtectedWithTwoFactor') : t('profile.addExtraSecurityLayer')}
                        </p>
                      </div>
                      
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        twoFactorEnabled 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {twoFactorEnabled ? t('profile.twoFactorEnabledStatus') : t('profile.twoFactorDisabledStatus')}
                      </div>
                    </div>
                    
                    <p className={`mb-6 text-sm ${subtleTextClass}`}>
                      {t('profile.twoFactorFullDescription')}
                    </p>
                    
                    {twoFactorEnabled ? (
                      <button
                        onClick={() => setDisabling2FA(true)}
                        className={`rounded-xl px-6 py-3 font-semibold ${dangerActionButtonClass}`}
                      >
                        {t('profile.disableTwoFactor')}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowTwoFactorSetup(true)}
                        className={`rounded-xl px-6 py-3 font-semibold ${primaryActionButtonClass}`}
                      >
                        {t('profile.enableTwoFactor')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <ReferralTab />
          )}
          
          {/* Support Tab */}
          {activeTab === 'giveaway' && (
            <GiveawaySection />
          )}

          {activeTab === 'support' && (
            <SupportChat user={user} />
          )}
        </div>
        
        {/* Right Column - Account Summary */}
        <div className="space-y-8">
          {/* Account Summary */}
          <div className={`p-6 ${primaryCardClass}`}>
            <h3 className="text-lg font-semibold text-white mb-4">{t('profile.accountSummary')}</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className={mutedTextClass}>{t('profile.accountType')}</span>
                <span className="text-white font-medium">{t('profile.standardAccountType')}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className={mutedTextClass}>{t('profile.memberSince')}</span>
                <span className="text-white font-medium">{new Date(user.created_at || Date.now()).toLocaleDateString()}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className={mutedTextClass}>{t('profile.kycStatus')}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  kycStatus === 'verified' ? 'bg-green-500/20 text-green-400' :
                  kycStatus === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>
                  {kycStatus === 'verified' ? 'Verified' :
                   kycStatus === 'pending' ? 'Pending' :
                   t('profile.notVerified')}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className={mutedTextClass}>{t('profile.twoFactorStatus')}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  twoFactorEnabled 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
            
            <div className="mt-6 border-t border-purple-500/20 pt-6">
              <button 
                onClick={() => setActiveTab('profile')}
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 font-semibold ${primaryActionButtonClass}`}
              >
                {t('profile.completeProfile')}
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          
          {/* Account Balances */}
          <div className={`p-6 ${primaryCardClass}`}>
            <h3 className="text-lg font-semibold text-white mb-4">{t('profile.accountBalances')}</h3>
            
            <div className="space-y-4">
              <div className={`p-4 ${innerCardClass}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500">
                      <span className="font-bold text-white">$</span>
                    </div>
                    <span className="text-white font-medium">USDT</span>
                  </div>
                  <span className="text-white font-mono">{(usdtBalance || 0).toFixed(2)}</span>
                </div>
                <div className={`text-right text-xs ${mutedTextClass}`}>
                  ${(usdtBalance || 0).toFixed(2)} USD
                </div>
              </div>
              
              <div className={`p-4 ${innerCardClass}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500">
                      <span className="font-bold text-white">B</span>
                    </div>
                    <span className="text-white font-medium">BTC</span>
                  </div>
                  <span className="text-white font-mono">{(btcBalance || 0).toFixed(8)}</span>
                </div>
                <div className={`text-right text-xs ${mutedTextClass}`}>
                  ${((btcBalance || 0) * (currentPrice || 0)).toFixed(2)} USD
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KYC Document Upload Modal */}
      {showKycUpload && (
        <KycDocumentUpload 
          onClose={() => setShowKycUpload(false)}
          onKycStatusChange={handleKycStatusChange}
        />
      )}
      </div>
    </div>
  );
};

export default ProfilePage;
